import { Token } from "@husqvarna/ui-core/dist/design-tokens/colors";
import { Area } from "../project/models/Area";
import { CuttingQuality, ProductType, ProjectStatus, ProjectType, SortOrder, UnitSystem } from "../project/models/Enums";
import { Product } from "../project/models/Product";
import { feature, polygon } from '@turf/helpers';
import length from '@turf/length';
import DashboardItem from "../project/models/DashboardItem";
import _ from "lodash";
import ProjectOverview from "../project/models/ProjectOverview";
import Project from "../project/models/Project";
import { GetMowerIconSrc } from "./components/MowerIcon/IconHelper";
import { calculateRemainingMowerCapacity } from "../project/coverageCalculations";

export const MAP_CONFIG = {
  mapId: "30513e6a765961a9",
  accessToken: "AIzaSyDRUTLhiDXkdZDwI11uFX_bvtK255xL1Ew",
  apiEndPoint: "/api/automap"
} as const;

export const getColorForStatus = (status: ProjectStatus): Token => {
  switch (status) {
    case ProjectStatus.InProgress:
      return "CautionYellow";
    case ProjectStatus.Completed:
      return "SuccessGreen";
    case ProjectStatus.Empty:
      return "Grey100";
  }
}

// Map enum values directly to numbers
export const cuttingQualityToNumber = {
  [CuttingQuality.Regular]: 0,
  [CuttingQuality.Pro]: 1,
  [CuttingQuality.ProSport]: 2,
};

// Map numbers back to enum values
export const numberToCuttingQuality: Record<number, CuttingQuality> = {
  0: CuttingQuality.Regular,
  1: CuttingQuality.Pro,
  2: CuttingQuality.ProSport,
};

export const getUnitSystemAsNumber = (project: Project): number => {
  // Default to Metric (0) if project is undefined or doesn't have a region
  // or if the unit system isn't explicitly Imperial
  if (!project.region?.unitSystem) return 0;
  
  switch (project.region.unitSystem) {
    case UnitSystem.Imperial:
      return 1;
    case UnitSystem.Metric:
    default:
      return 0;
  }
}

export const getDefaultMapLocation = (project: Project) => {
  if(!project.customerAddress && !project.customerCity && !project.customerZipCode) {
    return "";
  }
  const strings = [project?.customerAddress, project?.customerZipCode, project?.customerCity, project?.region.name];
  const filteredStrings = strings.filter(str => str !== "");
  const result = filteredStrings.join(', ');
  return result.trim();
}

//TODO move to different place?
export const createTestMower = (): Product => {
  const mower: Product = {
    id: "1",
    name: "test mower",
    kwhUsagePerMonth: 130,
    maxSlope: 30,
    maxActiveHoursPerDay: 24,
    maxAreaSizes: {} as Record<CuttingQuality, number>,
    canHandleMultipleQualities: true,
    price: {valueVatExcl: 0, currency: "SEK" },
    type: ProductType.Mower
  };
  return mower;
}

export const createTestArea = (id:string = "2", size:number = 500, cuttingQuality:CuttingQuality = CuttingQuality.Regular): Area => {
  const polygon = {
    type: "Polygon",
    coordinates: []
  }
  
  const area: Area = {
    id: id,
    name: "test area",
    size: size,
    cuttingQuality: cuttingQuality,
    maxSlope: 30,
    availableHoursToCutPerDay: 24,
    coveragePercent: 0,
    proposalProductConnections: [],
    polygonData: polygon,
    complexityPercentage: 0,
  };
  return area;
}

export const getCircumferenceForArea = (area:Area): number => {
  const newPolygon = polygon(area.polygonData.coordinates);
  const feat = feature(newPolygon.geometry);
  const circumference = length(feat, {units: "kilometers"});
  return Math.ceil(circumference * 1000);
}

export const getLengthForUnitSystem = (length: number, unitSystem: UnitSystem) : string  => {
  switch (unitSystem){
      case UnitSystem.Imperial: { 
          // Used in US market, convert with factor for feet.
          return `${new Intl.NumberFormat('en-US').format(Number((length * 3.2808399).toFixed(0)))} ft`;
      }
      default: {
        return `${new Intl.NumberFormat(navigator.language).format(length)} m`;             
      }
  }
}

export const getTotalAreaForUnitSystem = (totalArea: number, unitSystem: UnitSystem) : string  => { 
  switch (unitSystem){
      case UnitSystem.Imperial: { 
          // Used in US market, convert with factor for acre.          
        return new Intl.NumberFormat('en-US').format(Number((totalArea / 4046.85642).toFixed(2))) + " ac"
      }
      default: {          
        return new Intl.NumberFormat(navigator.language).format(Number((totalArea).toFixed(2))) + " m²"
      }
  }
}
export type SortDirection = "asc" | "desc";

export const sortDashboardItems = (projects: DashboardItem[] | undefined, sortOrder: SortOrder, sortDirection: SortDirection = "asc"): DashboardItem[] => {
  return sortFunction(projects, sortOrder, sortDirection) as DashboardItem[];
}
export const sortProjects = (projects: ProjectOverview[] | undefined, sortOrder: SortOrder, sortDirection: SortDirection = "asc"): ProjectOverview[] => {
  return sortFunction(projects, sortOrder, sortDirection) as ProjectOverview[];
}

const sortFunction = (projects: DashboardItem[] | ProjectOverview[] | undefined, sortOrder: SortOrder, sortDirection: SortDirection): DashboardItem[] | ProjectOverview[] | undefined => {
  if (projects && projects.length > 0) {
    const mutableProjects = _.cloneDeep(projects);
    const directionMultiplier = sortDirection === "desc" ? -1 : 1;
    mutableProjects?.sort((p1, p2) => {
      const date1 = new Date(p1.createdWhen).getTime();
      const date2 = new Date(p2.createdWhen).getTime();
      const updated1 = new Date(p1.updatedWhen).getTime();
      const updated2 = new Date(p2.updatedWhen).getTime();

      let result: number;
      switch (sortOrder) {
        case SortOrder.Status:
          // Use string comparison or explicit ordering
          if (p1.status === p2.status) result = 0;
          else if (p1.status === ProjectStatus.InProgress) result = -1;
          else if (p2.status === ProjectStatus.InProgress) result = 1;
          else if (p1.status === ProjectStatus.Empty && p2.status === ProjectStatus.Completed) result = -1;
          else result = 1;
          break;
        case SortOrder.LatestChange:
          result = updated2 - updated1;
          break;
        case SortOrder.Name:
          result = p1.name.localeCompare(p2.name);
          break;
        case SortOrder.Type:
          const type1 = ProjectType[p1.type ?? ProjectType.Other];
          const type2 = ProjectType[p2.type ?? ProjectType.Other];
          result = type1.localeCompare(type2);
          break;
        case SortOrder.AreaCount:
          result = p1.areaCount - p2.areaCount;
          break;
        case SortOrder.TotalAreaSize:
          result = p1.totalAreaSize - p2.totalAreaSize;
          break;
        default:
          result = 0;
      }
      return result * directionMultiplier;
    })

    return mutableProjects;
  }
  
  return projects;
}

/**
 * Gets the map-compatible area ID. For areas created via the map, this will be the externalId.
 */
export const getMapAreaId = (area: Area): string => {
  return area.externalId || area.id;
};

export const getMapDataFromProject = (project: Project) => {
  return {
          areas: project.areas.map((area) => ({
            ...area,
            id: getMapAreaId(area),
            equipment: getMapEquipment(area, project),
            cuttingQuality: cuttingQualityToNumber[area.cuttingQuality],
          })),
          unitSystem: getUnitSystemAsNumber(project),
          pointsOfInterest: project.pointsOfInterest ?? []
        };
}

export const getMapEquipment = (area: Area, project: Project) => {
  return area.proposalProductConnections
    .filter((prod) => prod.proposalProduct !== null)
    .map((prod) => {
      const areasConnectedToMower = project.areas.filter(area => prod.proposalProduct!.areaIds.includes(area.id));
      return {
        id: prod.proposalProduct!.id,
        name: shortenMowerName(prod.proposalProduct!.product.name),
        imgSrc: GetMowerIconSrc(prod.proposalProduct!.product.id),
        quantity: prod.proposalProduct!.quantity,
        color: prod.proposalProduct!.color,
        hasSufficientCapacity: calculateRemainingMowerCapacity(prod.proposalProduct!.product, prod.proposalProduct!.quantity, areasConnectedToMower, prod.possibleToMowOnDifferentHours, prod.proposalProduct!) >= 0
      }
    });
}

const shortenMowerName = (name: string) => {
  // CEORA™ 546 EPOS™ / RZ 43M / CS4 => CEORA™ 546 EPOS™...
  if (name.length > 16) {
    return name.substring(0, 16) + "...";
  }
  return name;
}

/**
 * Checks if two arrays are equal (same length and same elements in same order)
 * @param a First array
 * @param b Second array
 * @returns True if arrays are equal, false otherwise
 */
export const isArraysEqual = <T>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((val, index) => val === b[index]);
};